import type { FunctionComponent } from 'react'
import { useLocalSearchParams } from 'expo-router'
import {
  LegacyFreeze,
  LegacyFreezeParams,
} from '@/src/experiences/virtual-card/withFreeze/LegacyFreeze'
import { z } from 'zod/v4'
import { ID } from '@acre-rewards/lib'
import { routerBack } from '@/src/common/utils/routerBack'
import { VirtualCardApi } from '@/src/experiences/virtual-card/store'
import { useLogError } from '@/src/common/hooks/useLogError'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'

export interface FreezeProps {
  toggleFreeze: (isFrozen: boolean) => void | Promise<void>
  freezing: boolean
  unfreezing: boolean
}

export type FreezeComponent = FunctionComponent<FreezeProps>

const FreezeParams = z.union([
  z.object({ /** for new platform architecture */ cardAccountId: ID.schema.CardAccount }),
  LegacyFreezeParams,
])
export type FreezeParams = z.infer<typeof FreezeParams>

export function withFreeze(ToWrap: FreezeComponent) {
  return function Wrapped() {
    const params = FreezeParams.parse(useLocalSearchParams())

    return 'cardAccountId' in params ? (
      <PlatformFreeze ToWrap={ToWrap} cardAccountId={params.cardAccountId} />
    ) : (
      <LegacyFreeze paymentCardId={params.paymentCardId} ToWrap={ToWrap} />
    )
  }
}

function PlatformFreeze({
  ToWrap,
  cardAccountId,
}: {
  ToWrap: FreezeComponent
  cardAccountId: ID.CardAccount
}) {
  const logError = useLogError()
  const Alert = useAlert(PlatformFreeze.name)
  const [freezeCard, { isLoading: freezing }] =
    VirtualCardApi.endpoints['POST /card/:cardAccountId/freeze'].useMutation()
  const [unfreezeCard, { isLoading: unfreezing }] =
    VirtualCardApi.endpoints['POST /card/:cardAccountId/unfreeze'].useMutation()

  return (
    <ToWrap
      toggleFreeze={(isFrozen) => {
        const action = isFrozen ? unfreezeCard : freezeCard
        void action({ params: { cardAccountId } }).then((response) => {
          if (response.error) {
            const errorMessage = isFrozen ? 'Failed to unfreeze card' : 'Failed to freeze card'
            logError(errorMessage, response.error)
            Alert.alert(errorMessage, 'Please try again later.')
          } else {
            const action = isFrozen ? 'unfrozen' : 'frozen'
            Alert.alert(`Card ${action}`, `Your card has been ${action}.`)
            void routerBack()
          }
        })
      }}
      freezing={freezing}
      unfreezing={unfreezing}
    />
  )
}
